import axios from "axios";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
  faHome,
  faStar as faStarRegular,
  faStar as faStarSolid,
} from "@fortawesome/pro-duotone-svg-icons";
import { batch } from "@preact/signals-react";
import { Button, Chip, DropDownButton } from "@progress/kendo-react-buttons";
import { Pager } from "@progress/kendo-react-data-tools";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import { TextBox } from "@progress/kendo-react-inputs";
import { Breadcrumb, TileLayout } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";
import { plusIcon } from "@progress/kendo-svg-icons";
import { v4 as uuidv4 } from "uuid";

import DeleteButton from "../Buttons/DeleteButton";
import EditButton from "../Buttons/EditButton";
import HidePermissionsButton from "../Buttons/HidePermissionsButton";
import ShowPermissionsButton from "../Buttons/ShowPermissionsButton";
import { Colors } from "../Colors/Colors";
import {
  allDatasets,
  allTemplates,
  isAdmin,
  isDatasetFavorite,
  notification,
  toggleFavoriteDataset,
  userData,
  userDatasets,
} from "../Common/Store/CommonSignals";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import { selectedHamburgerIndex } from "../Navigation/HamburgerMenu/HamburgerMenuSignals";
import ConfirmDeleteDialog from "../Notification/ConfirmDeleteDialog";
import { userHasAccessToResource } from "../Utilities/AuthorizationUtil";
import { handleSignalRTrigger } from "../Utilities/CommonUtils";
import { datasetData, EmptyDataset, EmptyUserGroupDataset } from "./NewDatasetSignals";
import {
  inUseTemplate,
  isDeleting,
  pageState,
  search,
  selectedDatasetId,
  showDeleteDialog,
  showInUseDialog,
  skip,
  take,
  viewPerms,
} from "./ViewDatasetSignals";

const ValueItemRender = ({ dataItem }) => <div className="k-pl-4">{dataItem}</div>;

function ViewDataset() {
  const navigate = useNavigate();
  selectedHamburgerIndex.value = 4;
  const datasets = allDatasets.value;
  const templates = allTemplates.value;
  const userEmail = userData.value?.userEmail;
  const userIsAdmin = isAdmin.value;

  const checkForTemplateUse = (id) => {
    const template = templates.find((template) =>
      template.sections.find((section) =>
        section.fields.find((field) => field.properties["Dataset"]?.id === id)
      )
    );

    return template;
  };

  const handleDeleteDataset = async (id) => {
    selectedDatasetId.value = id;
    isDeleting.value = id;
    showDeleteDialog.value = true;
  };

  const handleCancelDelete = () => {
    showDeleteDialog.value = false;
    selectedDatasetId.value = null;
    isDeleting.value = null;
  };

  async function handleConfirmDelete() {
    showDeleteDialog.value = false;

    const template = await checkForTemplateUse(selectedDatasetId.value);
    inUseTemplate.value = template;

    if (template) {
      showInUseDialog.value = true;
      isDeleting.value = null;
      showDeleteDialog.value = false;
      return;
    }
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Datasets/${selectedDatasetId.value}?userId=${userEmail}`
      );
      notification.value = {
        show: true,
        type: "success",
        message: "Dataset deleted successfully!",
      };
      isDeleting.value = null;
      await handleSignalRTrigger("DELETE", "Dataset", { id: selectedDatasetId.value });
      allDatasets.value = allDatasets.value.filter(
        (dataset) => dataset.id !== selectedDatasetId.value
      );
    } catch (error) {
      console.log("Error deleting dataset:", error);
      isDeleting.value = null;
    }
  }

  const filteredDatasets = Array.isArray(userDatasets.value)
    ? datasets.filter((dataset) => {
        // Check all dataset values
        const hasMatchInValues = Object.values(dataset).some((value) =>
          value?.toString()?.toLowerCase()?.includes(search.value?.toLowerCase())
        );
        // Check tags
        const tags = dataset.tags || [];
        const hasMatchInTags = tags.some((tag) =>
          tag?.toLowerCase().includes(search.value?.toLowerCase())
        );
        return hasMatchInValues || hasMatchInTags;
      })
    : [];

  const items = filteredDatasets
    .filter((dataset) => {
      if (userIsAdmin) {
        return true;
      }
      return userHasAccessToResource(dataset, userEmail);
    })
    .map((dataset) => ({
      key: dataset.id,
      resizable: false,
      reordorable: false,
      viewPerms: false,
      style: { cursor: "default" },
      header: (
        <div
          className={` ${
            (dataset.datasetType || "").toLowerCase() === "table"
              ? "dataset-type-table"
              : "dataset-type-list"
          }`}
        >
          <div className="dataset-tile-header">
            <strong name={"lblDatasetHeaderName_" + dataset.datasetName}>
              {dataset.datasetName}
            </strong>
            <div>
              {isDeleting.value === dataset.id ? (
                <Loader
                  type="pulsing"
                  themeColor="primary"
                />
              ) : (
                <div
                  key={`${dataset.id}`}
                  className="align-columns"
                >
                  <ColorChangingIcon
                    key={`star-${dataset.id}-${isDatasetFavorite(dataset.id)}`}
                    icon={isDatasetFavorite(dataset.id) ? faStarSolid : faStarRegular}
                    initialPrimaryColor={isDatasetFavorite(dataset.id) ? "#FFA500" : "#999999"}
                    initialSecondaryColor={isDatasetFavorite(dataset.id) ? "#FFD700" : "#979797ff"}
                    initialSize={18}
                    hoverSize={18}
                    tooltip={
                      isDatasetFavorite(dataset.id) ? "Remove from favorites" : "Add to favorites"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavoriteDataset(dataset.id);
                    }}
                  />
                  <DeleteButton
                    onClick={() => handleDeleteDataset(dataset.id)}
                    name={"btnDatasetDelete_" + dataset.datasetName}
                    data={dataset}
                  />
                  <EditButton
                    onClick={() => {
                      // Add id's temporarily to existing dataset items, this is how changes are handled in other files, these will not be stored in DB
                      const datasetCopy = JSON.parse(JSON.stringify(dataset));
                      datasetCopy.values = datasetCopy.values.map((item) => {
                        if (datasetCopy.datasetType === "User Group") {
                          try {
                            const person = item.value ? JSON.parse(item.value) : null;
                            return {
                              id: uuidv4(),
                              person: person,
                            };
                          } catch (e) {
                            console.error("Error parsing user group item:", e);
                            return {
                              id: uuidv4(),
                              person: null,
                            };
                          }
                        }
                        return {
                          id: uuidv4(),
                          ...item,
                        };
                      });
                      datasetData.value = datasetCopy;
                      navigate(`/datasets/edit?datasetId=${dataset.id}`);
                    }}
                    name={"btnDatasetEdit_" + dataset.datasetName}
                    data={dataset}
                  />
                </div>
              )}
            </div>
          </div>

          <div
            className="dataset-tile-subheader"
            name={"lblDatasetID_" + dataset.datasetName}
          >
            {dataset.id}
          </div>
        </div>
      ),
      body: (
        <div className="card-body">
          <div className="card-info">
            <div>
              <strong>Description:</strong> {dataset.datasetDescription}{" "}
            </div>
            <div>
              <strong>Dataset Type:</strong> {dataset.datasetType}
            </div>
            <div>
              <strong>Owner:</strong> {dataset.owner}
            </div>
            {(dataset.tags || []).length > 0 && (
              <div
                className="k-mt-2 k-d-flex k-flex-wrap"
                style={{ gap: "4px" }}
              >
                {(dataset.tags || []).map((tag, index) => (
                  <Chip
                    key={tag + "_" + index}
                    text={tag}
                    size="small"
                  />
                ))}
              </div>
            )}
          </div>

          <div className="permissions-btn">
            <strong>Permissions:</strong>
            {!viewPerms.value[dataset.id] && (
              <ShowPermissionsButton
                onClick={() =>
                  (viewPerms.value = (prevPerms) => ({
                    ...prevPerms,
                    [dataset.id]: true,
                  }))
                }
              />
            )}
            {viewPerms.value[dataset.id] && (
              <HidePermissionsButton
                onClick={() =>
                  (viewPerms.value = (prevPerms) => ({
                    ...prevPerms,
                    [dataset.id]: false,
                  }))
                }
              />
            )}
          </div>
          {viewPerms.value[dataset.id] && (
            <div className="card-permissions">
              {dataset.coOwners.length > 0 && (
                <div>
                  <strong className="k-ml-5">Co-Owners</strong>{" "}
                  {dataset.coOwners.map((coOwner) => (
                    <div
                      key={coOwner.user}
                      className="k-ml-10"
                    >
                      {coOwner.user}
                    </div>
                  ))}
                </div>
              )}

              {dataset.sharedPermission.filter((sp) => sp.permission === "View").length > 0 && (
                <div>
                  <strong className="k-ml-5">View Permissions: </strong>
                  {dataset.sharedPermission
                    .filter((sp) => sp.permission === "View")
                    .map((sharedPermission) => (
                      <div
                        key={sharedPermission.entity}
                        className="k-ml-10"
                      >
                        {sharedPermission.entity}
                      </div>
                    ))}
                </div>
              )}

              {dataset.sharedPermission.filter((sp) => sp.permission === "Modify").length > 0 && (
                <div>
                  <strong className="k-ml-5">Modify Permissions: </strong>
                  {dataset.sharedPermission
                    .filter((sp) => sp.permission === "Modify")
                    .map((sharedPermission) => (
                      <div
                        key={sharedPermission.entity}
                        className="k-ml-10"
                      >
                        {sharedPermission.entity}
                      </div>
                    ))}
                </div>
              )}
              {dataset.sharedPermission.length === 0 && dataset.coOwners.length === 0 && (
                <div>
                  <strong className="k-ml-5">Dataset not shared with anyone.</strong>
                </div>
              )}
            </div>
          )}

          {!viewPerms.value[dataset.id] && (
            <div className="card-data-container">
              {["List", "Global Parameters"].includes(dataset.datasetType) ? (
                <div className="card-data-container">
                  <div className="k-ml-2">
                    <strong>Values:</strong>
                  </div>
                  <ListView
                    className="card-data"
                    data={dataset.values.map(
                      (row) => row.value + (row.filterValue ? " (" + row.filterValue + ")" : "")
                    )}
                    item={ValueItemRender}
                  />
                </div>
              ) : null}

              {dataset.datasetType === "User Group" ? (
                <div className="card-data-container">
                  <div className="k-ml-2">
                    <strong>People:</strong>
                  </div>
                  <ListView
                    className="card-data"
                    data={dataset.values.map((row) => {
                      const person = row.value ? JSON.parse(row.value) : null;
                      return person
                        ? `${person.displayName} (${person.email})`
                        : "No person selected";
                    })}
                    item={ValueItemRender}
                  />
                </div>
              ) : null}

              {dataset.datasetType === "List View" ? (
                <div className="card-data-container">
                  <div className="k-ml-2">
                    <strong>Fields:</strong>
                  </div>
                  <ListView
                    className="card-data"
                    data={dataset.fields.map(
                      (field) => field.fieldName + " (" + field.fieldType + ")"
                    )}
                    item={ValueItemRender}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      ),
    }));

  const paginatedItems = items.slice(skip.value, skip.value + take.value);

  const handleSearch = useCallback((e) => {
    search.value = e.target.value;
  }, []);

  const handleCreateDataset = ({ navigate, e }) => {
    switch (e.item) {
      case "New List":
        datasetData.value = {
          ...EmptyDataset,
          owner: userData.value.userEmail,
          datasetType: "List",
        };
        console.log(datasetData.value);
        navigate("/datasets/create");
        break;
      case "New Global Parameters":
        datasetData.value = {
          ...EmptyDataset,
          owner: userData.value.userEmail,
          datasetType: "Global Parameters",
        };
        console.log(datasetData.value);
        navigate("/datasets/create");
        break;
      case "New User Group":
        datasetData.value = {
          ...EmptyUserGroupDataset,
          owner: userData.value.userEmail,
        };
        console.log(datasetData.value);
        navigate("/datasets/create");
        break;
      default:
        break;
    }
  };

  const createDatasetButton = ({ navigate }) => {
    return (
      userData.value?.permissions?.Modify.includes("Create Dataset") && (
        <DropDownButton
          className="!k-rounded-0"
          items={["New List", "New Global Parameters", "New User Group"]}
          svgIcon={plusIcon}
          text="Create Dataset"
          component="Create Dataset"
          themeColor={"primary"}
          onItemClick={(e) => handleCreateDataset({ navigate, e })}
        />
      )
    );
  };

  return (
    <>
      <Breadcrumb
        style={{ backgroundColor: "transparent" }}
        data={[
          {
            id: "home",
            icon: (
              <ColorChangingIcon
                icon={faHome}
                initialPrimaryColor="#0078D4"
                initialSecondaryColor="#00aeef"
                initialSize={16}
                hoverSize={16}
              />
            ),
          },
          { id: "viewDatasets_Edge", text: "View Datasets" },
        ]}
        onItemSelect={(e) => {
          console.log(e);
          if (e.id === "home") {
            navigate("/");
          } else if (e.id.includes("viewDatasets")) {
            navigate("/datasets");
          }
        }}
      />
      <div className="view-container">
        <TextBox
          name="txtDatasetSearchBar"
          prefix={() => createDatasetButton({ navigate })}
          className="tilelayout-searchbar"
          placeholder="Search by any field..."
          onChange={handleSearch}
          value={search.value}
        />
        <TileLayout
          className="tile-layout"
          items={paginatedItems}
          columns={4}
          rows={1}
          autoFlow="row dense"
          ignoreDrag={(e) => {
            return e.target;
          }}
        />
        <Pager
          className="view-pager"
          skip={pageState.value.skip}
          take={pageState.value.take}
          total={items.length}
          buttonCount={pageState.value.buttonCount}
          info={pageState.value.info}
          type={pageState.value.type}
          previousNext={pageState.value.previousNext}
          onPageChange={(e) =>
            batch(() => {
              skip.value = e.skip;
              take.value = e.take;
            })
          }
        />
        {showDeleteDialog.value && (
          <ConfirmDeleteDialog
            content="dataset"
            handleCancelDelete={handleCancelDelete}
            handleConfirmDelete={handleConfirmDelete}
          />
        )}
        {showInUseDialog.value && (
          <Dialog
            title={"Error"}
            onClose={() => (showInUseDialog.value = false)}
          >
            <p style={{ margin: "25px", textAlign: "center", fontSize: 16 }}>
              <div>This dataset is used in template:</div>
              <strong>{inUseTemplate.value.templateName}</strong>
              <div>Please remove it from the template before deleting.</div>
            </p>
            <DialogActionsBar>
              <Button
                className="k-button k-mx-5"
                style={{ backgroundColor: Colors.secondary }}
                onClick={() => (showInUseDialog.value = false)}
              >
                OK
              </Button>
            </DialogActionsBar>
          </Dialog>
        )}
      </div>
    </>
  );
}

export default ViewDataset;
